// src/components/FeedbackButton.jsx
// Shared floating feedback button for all Narbodica apps.
// Shows a small button that opens a modal for submitting feedback.
// Types: bug, feature, complaint, praise, suggestion, new_app_idea
// Submits to /api/feedback (shared platform endpoint).

import { useState } from 'react';
import { API_BASE as API_URL } from '../apiConfig';


const TYPES = [
  { id: 'bug',          label: '🐛 Bug Report',      desc: 'Something is broken or not working' },
  { id: 'feature',      label: '✨ Feature Request',  desc: 'Something you\'d like to see added' },
  { id: 'complaint',    label: '😤 Complaint',        desc: 'Something that frustrated you' },
  { id: 'praise',       label: '🙌 Praise',           desc: 'Something you love about the app' },
  { id: 'suggestion',   label: '💡 Suggestion',       desc: 'An idea that could make things better' },
  { id: 'new_app_idea', label: '🚀 New App Idea',     desc: 'A new app for the Narbodica suite' },
];

export default function FeedbackButton({ app, accentColor = '#a78bfa', tokenKey = 'accessToken' }) {
  const [open, setOpen]           = useState(false);
  const [step, setStep]           = useState('type');   // type | form | done
  const [type, setType]           = useState('');
  const [title, setTitle]         = useState('');
  const [desc, setDesc]           = useState('');
  const [steps, setSteps]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  const reset = () => { setStep('type'); setType(''); setTitle(''); setDesc(''); setSteps(''); setError(''); };
  const close = () => { setOpen(false); reset(); };

  const submit = async () => {
    if (!desc.trim()) return setError('Please describe the issue');
    setSubmitting(true); setError('');
    try {
      const token = localStorage.getItem(tokenKey);
      const body = { app, type, title, description: desc };
      if (type === 'bug') { body.stepsToReproduce = steps; }
      body.context = { url: window.location.href, userAgent: navigator.userAgent };

      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) { setStep('done'); }
      else setError(data.message || 'Failed to submit');
    } catch (e) { setError('Network error — please try again'); }
    finally { setSubmitting(false); }
  };

  const T = {
    bg: '#0c0a0a', surface: '#18181b', border: '#27272a',
    text: '#fafafa', muted: '#71717a', dim: '#a1a1aa',
    font: "'Inter', sans-serif",
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        title="Send feedback"
        style={{
          position: 'fixed', bottom: 80, right: 16, zIndex: 200,
          width: 42, height: 42, borderRadius: '50%',
          background: accentColor + '20', border: `1.5px solid ${accentColor}50`,
          color: accentColor, fontSize: 16, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s', backdropFilter: 'blur(8px)',
          boxShadow: `0 4px 12px ${accentColor}20`,
        }}
        onMouseEnter={e => e.currentTarget.style.background = accentColor + '35'}
        onMouseLeave={e => e.currentTarget.style.background = accentColor + '20'}
      >💬</button>

      {/* Modal */}
      {open && (
        <div
          onClick={e => e.target === e.currentTarget && close()}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '0 0 20px',
            fontFamily: T.font,
          }}
        >
          <div style={{
            width: '100%', maxWidth: 420,
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 20, overflow: 'hidden',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.4)',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, color: T.text }}>
                  {step === 'done' ? 'Thanks! 🙌' : 'Send Feedback'}
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                  {step === 'type' && 'What kind of feedback is this?'}
                  {step === 'form' && TYPES.find(t => t.id === type)?.label}
                  {step === 'done' && 'Your feedback has been received'}
                </div>
              </div>
              <button onClick={close} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
            </div>

            <div style={{ padding: 20 }}>
              {/* Step 1 — type selection */}
              {step === 'type' && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {TYPES.map(t => (
                    <button key={t.id} onClick={() => { setType(t.id); setStep('form'); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                        background: '#09090b', border: `1px solid ${T.border}`, borderRadius: 12,
                        cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s',
                        color: T.text,
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = accentColor + '60'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                    >
                      <span style={{ fontSize: 20, width: 28, flexShrink: 0 }}>{t.label.split(' ')[0]}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label.split(' ').slice(1).join(' ')}</div>
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2 — form */}
              {step === 'form' && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.dim, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Title (optional)
                    </label>
                    <input value={title} onChange={e => setTitle(e.target.value)}
                      placeholder="Short summary..."
                      style={{ width: '100%', padding: '9px 12px', background: '#09090b', border: `1px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 13, fontFamily: T.font, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.dim, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Description *
                    </label>
                    <textarea value={desc} onChange={e => setDesc(e.target.value)}
                      placeholder={
                        type === 'bug' ? "What happened? What did you expect?" :
                        type === 'feature' ? "Describe the feature you'd like..." :
                        type === 'new_app_idea' ? "What app? What would it do? Who's it for?" :
                        "Tell us more..."
                      }
                      rows={4}
                      style={{ width: '100%', padding: '9px 12px', background: '#09090b', border: `1px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 13, fontFamily: T.font, resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                  {type === 'bug' && (
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.dim, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Steps to reproduce (optional)
                      </label>
                      <textarea value={steps} onChange={e => setSteps(e.target.value)}
                        placeholder="1. Open the app&#10;2. Tap on...&#10;3. The error appears"
                        rows={3}
                        style={{ width: '100%', padding: '9px 12px', background: '#09090b', border: `1px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 13, fontFamily: T.font, resize: 'vertical', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {error && <div style={{ fontSize: 12, color: '#f87171', padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8, border: '1px solid rgba(248,113,113,0.2)' }}>{error}</div>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setStep('type')}
                      style={{ flex: 1, padding: '10px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 10, color: T.dim, fontSize: 13, cursor: 'pointer', fontFamily: T.font }}>
                      Back
                    </button>
                    <button onClick={submit} disabled={submitting || !desc.trim()}
                      style={{ flex: 2, padding: '10px', background: accentColor, border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: submitting || !desc.trim() ? 'not-allowed' : 'pointer', opacity: submitting || !desc.trim() ? 0.6 : 1, fontFamily: T.font }}>
                      {submitting ? 'Sending...' : 'Send Feedback'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 — done */}
              {step === 'done' && (
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 14, color: T.text, marginBottom: 6, fontWeight: 600 }}>Feedback received!</div>
                  <div style={{ fontSize: 13, color: T.muted, marginBottom: 20, lineHeight: 1.5 }}>
                    Our team reviews all feedback. {type === 'bug' ? "We'll investigate the bug." : type === 'feature' || type === 'new_app_idea' ? "Great ideas are added to the roadmap." : "Thanks for taking the time."}
                  </div>
                  <button onClick={close} style={{ width: '100%', padding: '11px', background: accentColor, border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
